import type { PlaySlipPlay } from '#product/app/composables/playSlip'
import type { LotteryTicketType } from '#product/app/stores/lottery'

import {
  KnownProductType,
  type LotteryId,
  Status,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { EditorReviewPlaysModal } from '#components'
import { EDITOR_CHECKOUT_KEY, EDITOR_REVIEW_MODAL_SPACE_PX } from '#editor/app/constants/editor'
import { useEditorOptionsStore } from '#editor/app/stores/options'
import { isQuickPicked } from '#editor/app/utils/editorPlay'
import { QUERY_REVALIDATE } from '~/constants/queryParams'

const CONFIG_TIMEOUT = 10_000

export const useEditorCheckout = (lotteryId: LotteryId) => {
  const id = lotteryId.toLowerCase()
  const route = useRoute()
  const { navigateToLocalized, removeQueryParams } = useRouterUtils()
  const { pushBeginCheckoutEvent } = useTracking()
  const { createECommerceEventData } = useTrackingData()
  const { logTrackingError } = useTrackingErrorLog()
  const optionsStore = useEditorOptionsStore(lotteryId)
  const playStore = useEditorPlayStore(lotteryId)
  const price = useEditorPrice(lotteryId)
  const { config, currentPrice, flaggedCount, flaggedPlays, playCount, playInputs, pricedPlays } =
    price
  const { coupon } = useBonusCodeCondition(currentPrice)
  const { storePlaySlip } = useEditorStorePlaySlip(lotteryId, price)
  const { isLoading: loading, isStoring: storing } = useEditorPromoCodeLoading(lotteryId)
  // the duplicate slip modal resets the old play slip, the editor keeps its own plays
  const { handleError, openAccountVerificationModal } = useLotteryCheckoutError(
    lotteryId,
    `/${id}`,
    () => config.value && playStore.deleteAllPlays(config.value),
  )

  const { registerModal } = useModal()
  const {
    releaseSpaceBelow,
    scrollToCenteredPlay,
    scrollToPlay,
    waitForCollapsed,
    waitForScrollIdle,
  } = useEditorScroll()
  const spotlight = useEditorSpotlight()

  const disabled = computed(() => !config.value || !playCount.value)

  const reviewing = ref(false)
  let centering = false
  let disposed = false

  const endSpotlight = () => {
    spotlight.value = null
    releaseSpaceBelow()
  }

  watch(reviewing, (active) => {
    if (!active) endSpotlight()
  })

  onScopeDispose(() => {
    disposed = true
    endSpotlight()
  })

  // a single play is reviewed in place above the modal, centered if both fit, while the page is still scrollable
  const spotlightSinglePlay = async () => {
    const [single, ...others] = flaggedPlays.value
    if (!single || others.length) return true
    if (centering) return false

    centering = true
    try {
      await waitForCollapsed()
      await scrollToCenteredPlay(single.id, EDITOR_REVIEW_MODAL_SPACE_PX)
      await waitForScrollIdle()
    } finally {
      centering = false
    }

    // the plays may have changed or the page may be gone while scrolling
    const [current, ...added] = flaggedPlays.value
    if (disposed || current?.id !== single.id || added.length) {
      endSpotlight()
      return false
    }

    spotlight.value = single.id
    return true
  }

  const trackBeginCheckout = async () => {
    const info = coupon.value?.info
    try {
      pushBeginCheckoutEvent(
        await createECommerceEventData({
          currentPrice: currentPrice.value,
          lotteryId,
          plays: pricedPlays.value.map((play) => ({
            isComplete: true,
            isQuickPick: isQuickPicked(play),
            perPlayAddOn: play.perPlayAddOn?.value ? play.perPlayAddOn : null,
          })) as PlaySlipPlay[],
          promoCodes: info
            ? [{ name: info.title, type: info.value.type, value: info.value.amount / 100 }]
            : undefined,
          ticketType: optionsStore.ticketType as LotteryTicketType,
          type: KnownProductType.Lottery,
        }),
      )
    } catch (trackingError) {
      logTrackingError('begin_checkout', trackingError)
    }
  }

  const checkout = async () => {
    if (loading.value) return
    if (flaggedCount.value) {
      // collapsed, a flagged play shows its badge on the page and in the modal
      if (flaggedPlays.value.some(({ id }) => id === playStore.expandedPlayId)) {
        playStore.setExpandedPlay(null)
      }
      if (await spotlightSinglePlay()) reviewing.value = true
      return
    }

    storing.value = true
    trackBeginCheckout()

    try {
      if (!useAuth().isLoggedIn.value) {
        return await navigateToLocalized(
          createRedirectLocation({ path: '/login' }, `/checkout/redirect?p=${id}&backroute=/${id}`),
          { replace: true },
        )
      }

      if (!config.value?.allowedPlays.includes(playInputs.value.length)) {
        return handleError(new Error('validation.plays.outofbounds'))
      }

      const player = await useUserDataLazyQuery().load()
      if (!player || player.player.status !== Status.Verified) return openAccountVerificationModal()

      await storePlaySlip()
      await navigateToLocalized({ path: '/checkout', query: { p: id } })
    } catch (caught) {
      handleError(caught)
    } finally {
      storing.value = false
    }
  }

  const editManually = async () => {
    reviewing.value = false
    const playId = flaggedPlays.value[0]?.id
    if (!playId) return
    playStore.setExpandedPlay(playId)
    await scrollToPlay(playId, 'instant')
  }

  const quickPickFlagged = async () => {
    reviewing.value = false
    const currentConfig = config.value
    if (!currentConfig) return
    flaggedPlays.value.forEach(({ id }) => playStore.quickPick(id, currentConfig))
    // a running promo code check must not swallow the checkout the customer asked for; each tick lets
    // the recheck watcher in editorPromoCode react first, to the quick picked price or a dropped coupon
    await nextTick()
    while (loading.value) {
      await until(loading).toBe(false)
      await nextTick()
    }
    return checkout()
  }

  registerModal({
    component: EditorReviewPlaysModal,
    onClose: () => (reviewing.value = false),
    props: () => ({
      onEdit: editManually,
      onQuickPick: quickPickFlagged,
      plays: flaggedPlays.value,
    }),
    when: () => reviewing.value && !!flaggedCount.value,
  })

  onMounted(async () => {
    if (!route.query[QUERY_REVALIDATE]) return
    try {
      await until(config).toBeTruthy({ timeout: CONFIG_TIMEOUT })
      if (playInputs.value.length) await storePlaySlip()
    } catch (caught) {
      handleError(caught)
    }
    try {
      await removeQueryParams(QUERY_REVALIDATE)
    } catch {
      // a failed navigation must not break the page
    }
  })

  const editorCheckout = { checkout, disabled, loading }
  provide(EDITOR_CHECKOUT_KEY, editorCheckout)
  return editorCheckout
}
